import { useLocation } from 'react-router-dom';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import Routes from 'shared/enums/routes';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import styles from './page-tab.module.scss';

export type PageTabModel = {
  title: string;
  subRoute: Routes;
};

type PageTabProps = {
  onPageChanged?: (link: string) => void;
  pageTabs: PageTabModel[];
  mainRoute: string;
};

const PageTab = ({ onPageChanged, pageTabs, mainRoute }: Readonly<PageTabProps>): React.JSX.Element => {
  const location = useLocation();
  const { hasRoutePermission } = usePermissionAuthorization();

  const getFullLink = (subRoute: string): string => (mainRoute.endsWith('/') ? `${mainRoute}${subRoute}` : `${mainRoute}/${subRoute}`);

  const authorizedPageTabs = pageTabs.filter((tab) => hasRoutePermission(tab.subRoute));

  const buttonGroupStyle = {
    gap: '2px',
    '& .MuiToggleButtonGroup-grouped.MuiToggleButtonGroup-grouped': {
      borderRadius: '8px',
      border: `1px solid ${Constants.Colors.outline}`,
      marginLeft: 0,
      padding: '10px 16px',
      width: 'fit-content',
      '&.Mui-disabled, &.Mui-selected': {
        borderRadius: '20px',
        border: `1px solid ${Constants.Colors.outline}`,
      },
    },
  };

  return (
    <>
      {authorizedPageTabs.length > 1 && (
        <div className={styles.pageTabContainer}>
          <ToggleButtonGroup
            color="primary"
            value={location.pathname}
            exclusive
            onChange={(_, newValue) => (onPageChanged ? onPageChanged(newValue) : null)}
            aria-label="Platform"
            sx={buttonGroupStyle}
          >
            {authorizedPageTabs.map((pageTab) => {
              const fullLink = getFullLink(pageTab.subRoute);
              const isActive = fullLink === location.pathname;
              return (
                <ToggleButton
                  data-testid={`page-tab-${pageTab.title}`}
                  key={fullLink}
                  value={fullLink}
                  disabled={fullLink === location.pathname}
                  className={isActive ? styles.active : ''}
                >
                  <Typography variant="body1" className={styles.buttonContainer}>
                    {pageTab.title}
                  </Typography>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </div>
      )}
    </>
  );
};
export default PageTab;
